import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutGrid,
  Link as LinkIcon,
  Calendar,
  Settings,
  LogOut,
  ChevronRight,
  ChevronsLeft,
  Briefcase,
  Menu,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useMediaQuery } from '@/hooks/use-media-query';

interface SidebarLink {
  icon: React.ReactNode;
  label: string;
  path: string;
}

const links: SidebarLink[] = [
  {
    icon: <LayoutGrid className="h-5 w-5" />,
    label: 'Dashboard',
    path: '/dashboard'
  },
  {
    icon: <Briefcase className="h-5 w-5" />,
    label: 'Services',
    path: '/services'
  },
  {
    icon: <Calendar className="h-5 w-5" />,
    label: 'Calendar',
    path: '/calendar'
  },
  {
    icon: <Settings className="h-5 w-5" />,
    label: 'Settings',
    path: '/settings'
  }
];

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const SidebarContent = () => (
    <motion.div
      animate={{ 
        width: isCollapsed ? 80 : 256
      }}
      transition={{
        duration: 0.2
      }}
      className="h-screen bg-background/50 backdrop-blur-xl border-r border-border/50 flex flex-col"
    >
      {/* Logo Section */}
      <div className={cn(
        "p-6",
        isCollapsed && "px-4"
      )}>
        <div className="flex items-center space-x-2">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl shrink-0">
            C
          </div>
          {!isCollapsed && (
            <span className="text-xl font-semibold transition-opacity duration-200">
              CitaLoo
            </span>
          )}
        </div>
      </div>

      {/* Navigation Links */}
      <nav className="flex-1 px-3 py-4">
        <div className="space-y-1">
          {links.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  'group relative flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 hover:bg-primary/5',
                  isActive ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <span className="relative flex items-center gap-3">
                  {link.icon}
                  {!isCollapsed && (
                    <span className="transition-opacity duration-200">
                      {link.label}
                    </span>
                  )}
                </span>
                {!isCollapsed && (
                  <span className="relative ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                    <ChevronRight className="h-4 w-4" />
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Logout Button */}
      <div className="p-4 mt-auto">
        <button 
          onClick={handleLogout}
          className={cn(
            "flex items-center gap-2 px-3 py-3 w-full text-sm font-medium text-red-500 hover:text-red-600 hover:bg-red-500/5 rounded-xl transition-colors",
            isCollapsed && "justify-center"
          )}
        >
          <LogOut className="h-5 w-5 shrink-0" />
          {!isCollapsed && (
            <span className="transition-opacity duration-200">
              Logout
            </span>
          )}
        </button>
      </div>

      {/* Pro Badge */}
      {!isCollapsed && (
        <div className="p-4 border-t border-border/50">
          <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
            <span className="text-sm font-medium">Free Plan</span>
            <Link
              to="/pricing"
              className="text-xs font-medium text-primary hover:text-primary/90 transition-colors"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>
      )}
    </motion.div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-background border border-border/50 text-muted-foreground hover:text-foreground transition-all duration-200 hover:bg-primary/5 hover:border-primary/20"
        >
          {isMobileOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      )}

      {/* Desktop Collapse Button */}
      {!isMobile && (
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn(
            "fixed z-50 p-2 rounded-full bg-background border border-border/50 text-muted-foreground hover:text-foreground transition-all duration-200",
            "hover:bg-primary/5 hover:border-primary/20",
            isCollapsed 
              ? "left-[68px] top-7" 
              : "left-[244px] top-7"
          )}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronsLeft className="h-4 w-4" />
          )}
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && isMobileOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AnimatePresence>
        {(!isMobile || isMobileOpen) && (
          <motion.div
            initial={isMobile ? { x: '-100%' } : false}
            animate={isMobile ? { x: 0 } : false}
            exit={isMobile ? { x: '-100%' } : false}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className={cn(
              "fixed left-0 top-0 z-40",
              isMobile ? "w-[256px]" : ""
            )}
          >
            <SidebarContent />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar; 